import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Zap, Tag, Users, ToggleLeft, ToggleRight, Copy, X, Mail, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNER_PAGES = [
  { key: 'home',           label: 'Home' },
  { key: 'partner_signup', label: 'Partner Signup' },
  { key: 'login',          label: 'Login' },
  { key: 'all',            label: 'All Pages' },
];

const DEFAULT_SERVICES = [
  'VPS', 'SSL', 'Domain', 'Dedicated Server', 'Colocation',
  'Cloud', 'Shared Hosting', 'Email', 'SIP Connect', 'Bare Metal',
];

const generateCoupon = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const getStatus = (offer) => {
  if (!offer.is_active) return 'inactive';
  const now = new Date();
  const end   = offer.end_date   ? new Date(offer.end_date)   : null;
  const start = offer.start_date ? new Date(offer.start_date) : null;
  if (end && now > end)     return 'expired';
  if (start && now < start) return 'scheduled';
  return 'active';
};

const STATUS_STYLE = {
  active:    'bg-emerald-100 text-emerald-700 border-emerald-200',
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  expired:   'bg-red-100 text-red-600 border-red-200',
  inactive:  'bg-slate-100 text-slate-500 border-slate-200',
};

const StatusBadge = ({ offer }) => {
  const s = getStatus(offer);
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${STATUS_STYLE[s]}`}>
      {s}
    </span>
  );
};

/* ─── Color Picker Row ─────────────────────────────────────── */
const ColorField = ({ label, value, onChange }) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="h-9 w-10 rounded border border-slate-200 cursor-pointer p-0.5 flex-shrink-0"
      />
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        className="font-mono text-sm"
        maxLength={7}
      />
    </div>
  </div>
);

const EMPTY_EMAIL_TEMPLATE = {
  subject: '',
  heading: '',
  intro: '',
  redemption_steps: [
    'Log in to your Partner Portal',
    'Go to Wallet',
    'Enter this code in the Redeem Coupon section',
    'Admin will verify and credit the amount within 24 hours',
  ],
  expiry_text: '',
  footer_text: 'CloudSetu Partner Portal',
  primary_color: '#FF6B35',
};

/* ─── Email Preview ──────────────────────────────────────── */
function EmailPreview({ template }) {
  const { heading, intro, redemption_steps, expiry_text, footer_text, primary_color } = template;
  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", padding: '20px 16px', background: '#ffffff' }}>
      {heading && <h3 style={{ color: primary_color, marginBottom: '8px', fontSize: '15px', fontWeight: '700' }}>{heading}</h3>}
      {intro && <p style={{ color: '#374151', fontSize: '13px' }}>{intro}</p>}
      <div style={{ border: `2px dashed ${primary_color}`, borderRadius: '8px', padding: '16px', textAlign: 'center', margin: '12px 0' }}>
        <p style={{ color: '#6b7280', fontSize: '11px', margin: '0 0 6px' }}>Your Coupon Code</p>
        <p style={{ fontFamily: 'monospace', fontSize: '20px', fontWeight: '700', color: primary_color, letterSpacing: '2px', margin: '0' }}>XXXX-XXXX</p>
      </div>
      {redemption_steps.filter(s => s.trim()).length > 0 && (
        <>
          <p style={{ color: '#374151', fontSize: '12px', fontWeight: '600' }}>How to redeem:</p>
          <ol style={{ color: '#374151', fontSize: '12px', lineHeight: '1.8', paddingLeft: '16px' }}>
            {redemption_steps.filter(s => s.trim()).map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </>
      )}
      {expiry_text && <p style={{ color: '#ef4444', fontSize: '11px', marginTop: '12px' }}>{expiry_text}</p>}
      {footer_text && <p style={{ color: '#6b7280', fontSize: '11px', marginTop: '16px' }}>{footer_text}</p>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   PARTNER OFFER DIALOG
════════════════════════════════════════════════════════════ */
const EMPTY_PARTNER = {
  name: '', title: '', description: '',
  badge_text: '', badge_color: '#FF6B35', bg_color: '#FFF8F0', text_color: '#0F1E3D',
  wallet_credit_amount: '',
  signup_is_free: true, signup_amount: '', signup_button_text: 'Register Free',
  pages: [], display_type: 'banner',
  start_date: '', end_date: '',
  is_active: true,
  email_template: null,
  max_redemptions_unlimited: true,
  max_redemptions: 50,
};

function PartnerOfferDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_PARTNER);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      ...EMPTY_PARTNER, ...initial,
      wallet_credit_amount: initial.wallet_credit_amount ?? '',
      signup_amount: initial.signup_amount ?? '',
      start_date: initial.start_date ? initial.start_date.split('T')[0] : '',
      end_date:   initial.end_date   ? initial.end_date.split('T')[0]   : '',
      email_template: initial.email_template || null,
      max_redemptions_unlimited: initial.max_redemptions === null || initial.max_redemptions === undefined,
      max_redemptions: initial.max_redemptions ?? 50,
    } : EMPTY_PARTNER);
  }, [initial, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const togglePage = (key) => {
    if (key === 'all') {
      set('pages', form.pages.includes('all') ? [] : ['all']);
      return;
    }
    const base = form.pages.filter(p => p !== 'all');
    set('pages', base.includes(key) ? base.filter(p => p !== key) : [...base, key]);
  };

  const handleSignupToggle = (isFree) => {
    set('signup_is_free', isFree);
    set('signup_button_text', isFree ? 'Register Free' : 'Pay & Register');
    if (isFree) set('signup_amount', '');
  };

  const validate = () => {
    if (!form.name.trim())  { toast.error('Offer Name is required');    return false; }
    if (!form.title.trim()) { toast.error('Display Title is required'); return false; }
    if (form.wallet_credit_amount === '' || isNaN(Number(form.wallet_credit_amount))) {
      toast.error('Wallet Credit Amount is required'); return false;
    }
    if (!form.signup_is_free && (form.signup_amount === '' || isNaN(Number(form.signup_amount)) || Number(form.signup_amount) <= 0)) {
      toast.error('Signup Amount is required for Paid Signup'); return false;
    }
    if (!form.start_date) { toast.error('Start Date is required'); return false; }
    if (!form.end_date)   { toast.error('End Date is required');   return false; }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        wallet_credit_amount: parseFloat(form.wallet_credit_amount) || 0,
        signup_amount: parseFloat(form.signup_amount) || 0,
        start_date: form.start_date || null,
        end_date:   form.end_date   || null,
        max_redemptions: form.max_redemptions_unlimited ? null : (parseInt(form.max_redemptions) || 50),
        max_redemptions_unlimited: undefined,
      };
      delete payload.max_redemptions_unlimited;
      if (initial?.id) {
        await axios.put(`${API}/admin/partner-offers/${initial.id}`, payload);
      } else {
        await axios.post(`${API}/admin/partner-offers`, payload);
      }
      toast.success(`Offer ${initial?.id ? 'updated' : 'created'}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Edit Partner Offer' : 'Create Partner Offer'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-1">

          {/* Row 1: Name + Title */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Offer Name <span className="text-red-500">*</span></Label>
              <Input placeholder="e.g. Diwali 2024" value={form.name}
                onChange={e => set('name', e.target.value)} data-testid="partner-offer-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Display Title <span className="text-red-500">*</span></Label>
              <Input placeholder="e.g. 🎉 Diwali Special Offer!" value={form.title}
                onChange={e => set('title', e.target.value)} data-testid="partner-offer-title" />
            </div>
          </div>

          {/* Row 2: Description */}
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={2} placeholder="Short description of the offer…"
              value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          {/* Row 3: Badge Text + Badge Color + Bg Color + Text Color */}
          <div className="space-y-1.5">
            <Label>Badge Text</Label>
            <Input placeholder="e.g. LIMITED OFFER" value={form.badge_text}
              onChange={e => set('badge_text', e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <ColorField label="Badge Color"      value={form.badge_color} onChange={v => set('badge_color', v)} />
            <ColorField label="Background Color" value={form.bg_color}    onChange={v => set('bg_color', v)} />
            <ColorField label="Text Color"       value={form.text_color}  onChange={v => set('text_color', v)} />
          </div>

          {/* Row 4: Wallet Credit Amount */}
          <div className="space-y-1.5">
            <Label>Wallet Credit Amount <span className="text-red-500">*</span>
              <span className="text-slate-400 text-xs ml-1">(signup credit awarded, e.g. ₹5100)</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium text-sm">₹</span>
              <Input type="number" min="0" placeholder="5100"
                className="pl-7"
                value={form.wallet_credit_amount}
                onChange={e => set('wallet_credit_amount', e.target.value)}
                data-testid="partner-offer-credit" />
            </div>
          </div>

          {/* Row 5: Signup Button Control */}
          <div className="space-y-2 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <Label className="text-sm font-semibold text-slate-700">Signup Button Control</Label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleSignupToggle(true)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all
                  ${form.signup_is_free
                    ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                data-testid="signup-type-free"
              >
                Free Signup
              </button>
              <button
                type="button"
                onClick={() => handleSignupToggle(false)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all
                  ${!form.signup_is_free
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                data-testid="signup-type-paid"
              >
                Paid Signup
              </button>
            </div>
            {/* Signup Amount — only shown for Paid Signup */}
            {!form.signup_is_free && (
              <div className="space-y-1.5 pt-2">
                <Label className="text-xs text-slate-600 font-semibold">Registration Fee (₹) <span className="text-red-500">*</span>
                  <span className="text-slate-400 font-normal ml-1">(amount partner pays to register)</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium text-sm">₹</span>
                  <Input type="number" min="1" placeholder="5000"
                    className="pl-7"
                    value={form.signup_amount}
                    onChange={e => set('signup_amount', e.target.value)}
                    data-testid="partner-offer-signup-amount" />
                </div>
              </div>
            )}
            <div className="space-y-1.5 pt-1">
              <Label className="text-xs text-slate-500">Button Text</Label>
              <Input
                placeholder={form.signup_is_free ? 'Register Free' : 'Pay & Register'}
                value={form.signup_button_text}
                onChange={e => set('signup_button_text', e.target.value)}
                data-testid="partner-offer-btn-text"
              />
            </div>
          </div>

          {/* Row 6: Show On Pages */}
          <div className="space-y-2">
            <Label>Show On Pages</Label>
            <div className="flex flex-wrap gap-4">
              {PARTNER_PAGES.map(p => (
                <label key={p.key} className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={form.pages.includes(p.key)}
                    onChange={() => togglePage(p.key)}
                    className="rounded border-slate-300 text-blue-600 w-4 h-4" />
                  <span className="text-sm text-slate-700">{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Row 7: Display Type */}
          <div className="space-y-2">
            <Label>Display Type</Label>
            <div className="flex gap-3">
              {['banner', 'popup', 'both'].map(dt => (
                <button
                  key={dt}
                  type="button"
                  onClick={() => set('display_type', dt)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border capitalize transition-all
                    ${form.display_type === dt
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                  data-testid={`display-type-${dt}`}
                >
                  {dt}
                </button>
              ))}
            </div>
          </div>

          {/* Row 8: Start + End Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Start Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)}
                data-testid="partner-offer-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label>End Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)}
                data-testid="partner-offer-end-date" />
            </div>
          </div>

          {/* Row 8.5: Max Redemptions */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Max Redemptions</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => set('max_redemptions_unlimited', true)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all
                  ${form.max_redemptions_unlimited
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                data-testid="max-unlimited-btn"
              >
                ∞ Unlimited
              </button>
              <button
                type="button"
                onClick={() => set('max_redemptions_unlimited', false)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all
                  ${!form.max_redemptions_unlimited
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                data-testid="max-limited-btn"
              >
                Limited
              </button>
            </div>
            {!form.max_redemptions_unlimited && (
              <Input
                type="number"
                min="1"
                value={form.max_redemptions}
                onChange={e => set('max_redemptions', e.target.value)}
                placeholder="e.g. 50"
                className="max-w-[180px]"
                data-testid="max-redemptions-input"
              />
            )}
            <p className="text-xs text-slate-500">
              {form.max_redemptions_unlimited
                ? 'No cap — all partners can receive and redeem coupons.'
                : `First ${form.max_redemptions || '?'} approved redemptions will be credited.`}
            </p>
          </div>

          {/* Row 9: Active toggle */}
          <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
            <Switch checked={form.is_active} onCheckedChange={v => set('is_active', v)} id="po-active" />
            <Label htmlFor="po-active" className="cursor-pointer font-medium">
              {form.is_active ? 'Active' : 'Inactive'}
            </Label>
          </div>

          {/* Row 10: Email Template Settings */}
          <div className="space-y-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-500" /> Email Template
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  When enabled, new partners get a coupon code via email at signup.
                </p>
              </div>
              <Switch
                checked={form.email_template !== null}
                onCheckedChange={v => set('email_template', v ? { ...EMPTY_EMAIL_TEMPLATE } : null)}
                id="po-email-tmpl"
                data-testid="email-template-toggle"
              />
            </div>

            {form.email_template !== null && (
              <div className="space-y-4 pt-3 border-t border-blue-200">
                {/* Subject */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">
                    Email Subject <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    placeholder="e.g. Your Exclusive Coupon Code — CloudSetu"
                    value={form.email_template.subject}
                    onChange={e => set('email_template', { ...form.email_template, subject: e.target.value })}
                    data-testid="email-tmpl-subject"
                  />
                </div>

                {/* Heading + Color */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Email Heading</Label>
                    <Input
                      placeholder="e.g. Exclusive Offer!"
                      value={form.email_template.heading}
                      onChange={e => set('email_template', { ...form.email_template, heading: e.target.value })}
                      data-testid="email-tmpl-heading"
                    />
                  </div>
                  <ColorField
                    label="Primary Color"
                    value={form.email_template.primary_color}
                    onChange={v => set('email_template', { ...form.email_template, primary_color: v })}
                  />
                </div>

                {/* Intro */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Intro Text</Label>
                  <Textarea
                    rows={2}
                    placeholder="e.g. As a new CloudSetu Partner, you've been assigned an exclusive coupon!"
                    value={form.email_template.intro}
                    onChange={e => set('email_template', { ...form.email_template, intro: e.target.value })}
                    data-testid="email-tmpl-intro"
                  />
                </div>

                {/* Redemption Steps */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-700">Redemption Steps</Label>
                  {form.email_template.redemption_steps.map((step, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <span className="text-xs text-slate-400 w-5 text-center flex-shrink-0">{idx + 1}.</span>
                      <Input
                        value={step}
                        placeholder={`Step ${idx + 1}`}
                        onChange={e => {
                          const steps = [...form.email_template.redemption_steps];
                          steps[idx] = e.target.value;
                          set('email_template', { ...form.email_template, redemption_steps: steps });
                        }}
                        className="text-sm"
                        data-testid={`email-tmpl-step-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const steps = form.email_template.redemption_steps.filter((_, i) => i !== idx);
                          set('email_template', { ...form.email_template, redemption_steps: steps });
                        }}
                        className="text-slate-400 hover:text-red-500 flex-shrink-0 p-1"
                        data-testid={`remove-step-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => set('email_template', { ...form.email_template, redemption_steps: [...form.email_template.redemption_steps, ''] })}
                    data-testid="add-redemption-step-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Step
                  </Button>
                </div>

                {/* Expiry Text + Footer */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Expiry Text</Label>
                    <Input
                      placeholder="e.g. Coupon expires on Dec 31, 2026."
                      value={form.email_template.expiry_text}
                      onChange={e => set('email_template', { ...form.email_template, expiry_text: e.target.value })}
                      data-testid="email-tmpl-expiry"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-700">Footer Text</Label>
                    <Input
                      placeholder="e.g. CloudSetu Partner Portal"
                      value={form.email_template.footer_text}
                      onChange={e => set('email_template', { ...form.email_template, footer_text: e.target.value })}
                      data-testid="email-tmpl-footer"
                    />
                  </div>
                </div>

                {/* Live Email Preview */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <Eye className="h-3.5 w-3.5" /> Email Preview
                  </Label>
                  <div className="border border-slate-200 rounded-lg overflow-hidden bg-white max-h-60 overflow-y-auto">
                    <EmailPreview template={form.email_template} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="partner-offer-save-btn">
            {saving ? 'Saving…' : (initial?.id ? 'Update Offer' : 'Create Offer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ════════════════════════════════════════════════════════════
   CLIENT OFFER DIALOG
════════════════════════════════════════════════════════════ */
const EMPTY_CLIENT = {
  name: '', title: '', description: '',
  discount_type: 'flat',
  discount_value: '', percentage_value: '',
  max_discount_cap: '', min_order_amount: '',
  applicable_services: [],
  coupon_code: '', usage_limit: '', per_user_limit: '1',
  start_date: '', end_date: '',
  show_on_login: false, show_in_dashboard: true,
  is_active: true,
};

function ClientOfferDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm]           = useState(EMPTY_CLIENT);
  const [saving, setSaving]       = useState(false);
  const [customService, setCustom] = useState('');
  // Available services list (default + any custom ones already on the offer)
  const [serviceList, setServiceList] = useState(DEFAULT_SERVICES);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      // Merge any custom services already saved into the list
      const extra = (initial.applicable_services || []).filter(s => !DEFAULT_SERVICES.includes(s) && s !== 'All Services');
      setServiceList([...DEFAULT_SERVICES, ...extra]);
      setForm({
        ...EMPTY_CLIENT, ...initial,
        discount_value:   initial.discount_value   ?? '',
        percentage_value: initial.percentage_value ?? '',
        max_discount_cap: initial.max_discount_cap ?? '',
        min_order_amount: initial.min_order_amount ?? '',
        usage_limit:      initial.usage_limit      ?? '',
        per_user_limit:   initial.per_user_limit   ?? '1',
        start_date: initial.start_date ? initial.start_date.split('T')[0] : '',
        end_date:   initial.end_date   ? initial.end_date.split('T')[0]   : '',
      });
    } else {
      setServiceList(DEFAULT_SERVICES);
      setForm(EMPTY_CLIENT);
    }
    setCustom('');
  }, [initial, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleService = (s) => {
    if (s === 'All Services') {
      set('applicable_services',
        form.applicable_services.includes('All Services') ? [] : ['All Services']);
      return;
    }
    const without = form.applicable_services.filter(x => x !== 'All Services');
    set('applicable_services',
      without.includes(s) ? without.filter(x => x !== s) : [...without, s]);
  };

  const addCustomService = () => {
    const name = customService.trim();
    if (!name) return;
    if (serviceList.includes(name)) {
      // Just select it
      if (!form.applicable_services.includes(name)) {
        set('applicable_services', [...form.applicable_services.filter(x => x !== 'All Services'), name]);
      }
    } else {
      setServiceList(prev => [...prev, name]);
      set('applicable_services', [...form.applicable_services.filter(x => x !== 'All Services'), name]);
    }
    setCustom('');
  };

  const validate = () => {
    if (!form.name.trim())  { toast.error('Offer Name is required');    return false; }
    if (!form.title.trim()) { toast.error('Display Title is required'); return false; }
    if (!form.coupon_code.trim()) { toast.error('Coupon Code is required'); return false; }
    if (!form.start_date)   { toast.error('Start Date is required');    return false; }
    if (!form.end_date)     { toast.error('End Date is required');      return false; }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_value:   parseFloat(form.discount_value)   || 0,
        percentage_value: parseFloat(form.percentage_value) || 0,
        max_discount_cap: form.max_discount_cap ? parseFloat(form.max_discount_cap) : null,
        min_order_amount: parseFloat(form.min_order_amount) || 0,
        usage_limit:      form.usage_limit ? parseInt(form.usage_limit) : null,
        per_user_limit:   parseInt(form.per_user_limit) || 1,
        start_date: form.start_date || null,
        end_date:   form.end_date   || null,
      };
      if (initial?.id) {
        await axios.put(`${API}/admin/client-offers/${initial.id}`, payload);
      } else {
        await axios.post(`${API}/admin/client-offers`, payload);
      }
      toast.success(`Offer ${initial?.id ? 'updated' : 'created'}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const showFlat       = form.discount_type === 'flat'       || form.discount_type === 'both';
  const showPercentage = form.discount_type === 'percentage' || form.discount_type === 'both';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Edit Client Offer' : 'Create Client Offer'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-1">

          {/* 1 + 2: Name + Title */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Offer Name <span className="text-red-500">*</span></Label>
              <Input placeholder="e.g. Diwali Client Deal" value={form.name}
                onChange={e => set('name', e.target.value)} data-testid="client-offer-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Display Title <span className="text-red-500">*</span></Label>
              <Input placeholder="e.g. Festive Discount" value={form.title}
                onChange={e => set('title', e.target.value)} data-testid="client-offer-title" />
            </div>
          </div>

          {/* 3: Description */}
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          {/* 4: Discount Type radio + values */}
          <div className="space-y-2">
            <Label>Discount Type</Label>
            <div className="flex gap-3">
              {[{ v: 'flat', l: 'Flat ₹' }, { v: 'percentage', l: 'Percentage %' }, { v: 'both', l: 'Both' }].map(({ v, l }) => (
                <button key={v} type="button"
                  onClick={() => set('discount_type', v)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all
                    ${form.discount_type === v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                  data-testid={`discount-type-${v}`}
                >{l}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 pt-1">
              {showFlat && (
                <div className="space-y-1.5">
                  <Label>Flat Discount Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">₹</span>
                    <Input type="number" min="0" placeholder="0" className="pl-7"
                      value={form.discount_value} onChange={e => set('discount_value', e.target.value)} />
                  </div>
                </div>
              )}
              {showPercentage && (
                <div className="space-y-1.5">
                  <Label>Discount Percentage</Label>
                  <div className="relative">
                    <Input type="number" min="0" max="100" placeholder="0" className="pr-7"
                      value={form.percentage_value} onChange={e => set('percentage_value', e.target.value)} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">%</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 5: Applicable Services */}
          <div className="space-y-2">
            <Label>Applicable Services
              <span className="text-slate-400 text-xs ml-1">(leave empty = all)</span>
            </Label>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {/* All Services shortcut */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox"
                  checked={form.applicable_services.includes('All Services')}
                  onChange={() => toggleService('All Services')}
                  className="rounded border-slate-300 text-blue-600 w-4 h-4" />
                <span className="text-sm font-semibold text-blue-600">All Services</span>
              </label>
              {serviceList.map(s => (
                <label key={s} className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox"
                    checked={form.applicable_services.includes(s)}
                    onChange={() => toggleService(s)}
                    disabled={form.applicable_services.includes('All Services')}
                    className="rounded border-slate-300 text-blue-600 w-4 h-4" />
                  <span className="text-sm text-slate-700">{s}</span>
                </label>
              ))}
            </div>
            {/* Add custom service */}
            <div className="flex gap-2 pt-1">
              <Input
                placeholder="Add custom service…"
                value={customService}
                onChange={e => setCustom(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomService())}
                className="text-sm"
                data-testid="custom-service-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addCustomService} data-testid="add-custom-service-btn">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* 6 + 7: Min Order + Max Cap */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Minimum Order Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">₹</span>
                <Input type="number" min="0" placeholder="0" className="pl-7"
                  value={form.min_order_amount} onChange={e => set('min_order_amount', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Maximum Discount Cap</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">₹</span>
                <Input type="number" min="0" placeholder="No cap" className="pl-7"
                  value={form.max_discount_cap} onChange={e => set('max_discount_cap', e.target.value)} />
              </div>
            </div>
          </div>

          {/* 8: Coupon Code */}
          <div className="space-y-1.5">
            <Label>Coupon Code <span className="text-red-500">*</span></Label>
            <div className="flex gap-2">
              <Input className="uppercase font-mono tracking-widest" placeholder="e.g. DIWALI50"
                value={form.coupon_code}
                onChange={e => set('coupon_code', e.target.value.toUpperCase())}
                data-testid="client-offer-coupon" />
              <Button type="button" variant="outline" onClick={() => set('coupon_code', generateCoupon())}
                data-testid="auto-generate-coupon-btn">
                <Zap className="h-4 w-4 mr-1" /> Auto
              </Button>
            </div>
          </div>

          {/* 9 + 10: Usage + Per User Limit */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Usage Limit <span className="text-slate-400 text-xs">(total uses)</span></Label>
              <Input type="number" min="1" placeholder="Unlimited"
                value={form.usage_limit} onChange={e => set('usage_limit', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Per User Limit</Label>
              <Input type="number" min="1" placeholder="1"
                value={form.per_user_limit} onChange={e => set('per_user_limit', e.target.value)} />
            </div>
          </div>

          {/* 11 + 12: Start + End Date (REQUIRED) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Start Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={form.start_date}
                onChange={e => set('start_date', e.target.value)} data-testid="client-offer-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label>End Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={form.end_date}
                onChange={e => set('end_date', e.target.value)} data-testid="client-offer-end-date" />
            </div>
          </div>

          {/* 13 + 14: Visibility Toggles */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3">
              <Switch checked={form.show_on_login} onCheckedChange={v => set('show_on_login', v)} id="co-login" />
              <Label htmlFor="co-login" className="cursor-pointer text-sm">Show on Client Login</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.show_in_dashboard} onCheckedChange={v => set('show_in_dashboard', v)} id="co-dash" />
              <Label htmlFor="co-dash" className="cursor-pointer text-sm">Show in Client Dashboard</Label>
            </div>
          </div>

          {/* 15: Active toggle */}
          <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
            <Switch checked={form.is_active} onCheckedChange={v => set('is_active', v)} id="co-active" />
            <Label htmlFor="co-active" className="cursor-pointer font-medium">
              {form.is_active ? 'Active' : 'Inactive'}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="client-offer-save-btn">
            {saving ? 'Saving…' : (initial?.id ? 'Update Offer' : 'Create Offer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Delete Confirm ─────────────────────────────────────── */
function DeleteConfirm({ open, name, onConfirm, onClose, loading }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Delete Offer</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">
          Delete <strong>{name}</strong>? This cannot be undone.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ════════════════════════════════════════════════════════════
   PARTNER OFFERS TAB
════════════════════════════════════════════════════════════ */
function PartnerOffersTab({ onCountChange }) {
  const [offers, setOffers]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dialog, setDialog]       = useState(null);
  const [deleteTarget, setDelete] = useState(null);
  const [deleting, setDeleting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await axios.get(`${API}/admin/partner-offers`)).data;
      setOffers(data);
      onCountChange(data.length);
    } catch { toast.error('Failed to load partner offers'); }
    finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (offer) => {
    try {
      const res = await axios.patch(`${API}/admin/partner-offers/${offer.id}/toggle`);
      setOffers(prev => prev.map(o => o.id === offer.id ? { ...o, is_active: res.data.is_active } : o));
      toast.success(res.data.is_active ? 'Offer activated' : 'Offer deactivated');
    } catch { toast.error('Toggle failed'); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/partner-offers/${deleteTarget.id}`);
      setOffers(prev => prev.filter(o => o.id !== deleteTarget.id));
      onCountChange(offers.length - 1);
      toast.success('Offer deleted');
      setDelete(null);
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{offers.length} offer{offers.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => setDialog('create')} data-testid="create-partner-offer-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Offer
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : offers.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Tag className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No partner offers yet</p>
          <p className="text-sm mt-1">Create your first offer to get started</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Offer', 'Credit', 'Signup', 'Type', 'Pages', 'Dates', 'Status', 'Active', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {offers.map(offer => (
                <tr key={offer.id} className="hover:bg-slate-50 transition-colors" data-testid={`partner-offer-row-${offer.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{offer.name}</div>
                    <div className="text-xs text-slate-400 truncate max-w-[160px]">{offer.title}</div>
                    {offer.badge_text && (
                      <span className="mt-1 inline-block px-1.5 py-0.5 rounded text-xs font-semibold"
                        style={{ background: offer.bg_color, color: offer.badge_color }}>
                        {offer.badge_text}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold text-emerald-700 whitespace-nowrap">
                    ₹{(offer.wallet_credit_amount || 0).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${offer.signup_is_free ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                      {offer.signup_is_free ? 'Free' : `Paid ₹${(offer.signup_amount || 0).toLocaleString('en-IN')}`}
                    </span>
                    <div className="text-xs text-slate-400 mt-0.5 truncate max-w-[100px]">{offer.signup_button_text}</div>
                  </td>
                  <td className="px-4 py-3 capitalize">
                    <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-medium">{offer.display_type}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{(offer.pages || []).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                    {offer.start_date ? offer.start_date.split('T')[0] : '—'}<br />
                    {offer.end_date   ? offer.end_date.split('T')[0]   : '—'}
                  </td>
                  <td className="px-4 py-3"><StatusBadge offer={offer} /></td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(offer)} className="focus:outline-none" data-testid={`partner-toggle-${offer.id}`}>
                      {offer.is_active
                        ? <ToggleRight className="h-6 w-6 text-emerald-500" />
                        : <ToggleLeft  className="h-6 w-6 text-slate-300" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialog(offer)} data-testid={`partner-edit-${offer.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDelete(offer)} data-testid={`partner-delete-${offer.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PartnerOfferDialog
        open={dialog !== null}
        initial={dialog === 'create' ? null : dialog}
        onClose={() => setDialog(null)}
        onSaved={load}
      />
      <DeleteConfirm open={deleteTarget !== null} name={deleteTarget?.name}
        onConfirm={remove} onClose={() => setDelete(null)} loading={deleting} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   CLIENT OFFERS TAB
════════════════════════════════════════════════════════════ */
function ClientOffersTab({ onCountChange }) {
  const [offers, setOffers]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dialog, setDialog]       = useState(null);
  const [deleteTarget, setDelete] = useState(null);
  const [deleting, setDeleting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await axios.get(`${API}/admin/client-offers`)).data;
      setOffers(data);
      onCountChange(data.length);
    } catch { toast.error('Failed to load client offers'); }
    finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (offer) => {
    try {
      const res = await axios.patch(`${API}/admin/client-offers/${offer.id}/toggle`);
      setOffers(prev => prev.map(o => o.id === offer.id ? { ...o, is_active: res.data.is_active } : o));
      toast.success(res.data.is_active ? 'Offer activated' : 'Offer deactivated');
    } catch { toast.error('Toggle failed'); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/client-offers/${deleteTarget.id}`);
      setOffers(prev => prev.filter(o => o.id !== deleteTarget.id));
      onCountChange(offers.length - 1);
      toast.success('Offer deleted');
      setDelete(null);
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied!');
  };

  const discountLabel = (o) => {
    if (o.discount_type === 'flat')       return `₹${o.discount_value}`;
    if (o.discount_type === 'percentage') return `${o.percentage_value}%`;
    return `₹${o.discount_value} + ${o.percentage_value}%`;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{offers.length} offer{offers.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => setDialog('create')} data-testid="create-client-offer-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Offer
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : offers.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No client offers yet</p>
          <p className="text-sm mt-1">Create discount offers for your clients</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Offer', 'Discount', 'Coupon Code', 'Used / Limit', 'Services', 'Dates', 'Status', 'Active', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {offers.map(offer => (
                <tr key={offer.id} className="hover:bg-slate-50 transition-colors" data-testid={`client-offer-row-${offer.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{offer.name}</div>
                    <div className="text-xs text-slate-400 truncate max-w-[140px]">{offer.title}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs font-semibold">{discountLabel(offer)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <code className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono font-bold tracking-widest">{offer.coupon_code}</code>
                      <button onClick={() => copyCode(offer.coupon_code)} className="text-slate-400 hover:text-blue-500" title="Copy">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs font-medium">
                    {offer.used_count ?? 0} / {offer.usage_limit ?? '∞'}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {(offer.applicable_services || []).length === 0
                      ? 'All'
                      : offer.applicable_services.includes('All Services')
                        ? 'All'
                        : `${offer.applicable_services.slice(0, 2).join(', ')}${offer.applicable_services.length > 2 ? ` +${offer.applicable_services.length - 2}` : ''}`
                    }
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                    {offer.start_date ? offer.start_date.split('T')[0] : '—'}<br />
                    {offer.end_date   ? offer.end_date.split('T')[0]   : '—'}
                  </td>
                  <td className="px-4 py-3"><StatusBadge offer={offer} /></td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(offer)} className="focus:outline-none" data-testid={`client-toggle-${offer.id}`}>
                      {offer.is_active
                        ? <ToggleRight className="h-6 w-6 text-emerald-500" />
                        : <ToggleLeft  className="h-6 w-6 text-slate-300" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialog(offer)} data-testid={`client-edit-${offer.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDelete(offer)} data-testid={`client-delete-${offer.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ClientOfferDialog
        open={dialog !== null}
        initial={dialog === 'create' ? null : dialog}
        onClose={() => setDialog(null)}
        onSaved={load}
      />
      <DeleteConfirm open={deleteTarget !== null} name={deleteTarget?.name}
        onConfirm={remove} onClose={() => setDelete(null)} loading={deleting} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   CLIENT SIGNUP OFFER DIALOG (Referral Bonus Configuration)
════════════════════════════════════════════════════════════ */
const EMPTY_SIGNUP_OFFER = {
  name: '', description: '',
  referee_credit_amount: '500',
  referrer_credit_amount: '500',
  is_active: true,
  start_date: '', end_date: '',
};

function ClientSignupOfferDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_SIGNUP_OFFER);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      ...EMPTY_SIGNUP_OFFER, ...initial,
      referee_credit_amount: initial.referee_credit_amount ?? '500',
      referrer_credit_amount: initial.referrer_credit_amount ?? '500',
      start_date: initial.start_date ? initial.start_date.split('T')[0] : '',
      end_date:   initial.end_date   ? initial.end_date.split('T')[0]   : '',
    } : EMPTY_SIGNUP_OFFER);
  }, [initial, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) { toast.error('Offer Name is required'); return false; }
    if (form.referee_credit_amount === '' || isNaN(Number(form.referee_credit_amount))) {
      toast.error('Referee Credit Amount is required'); return false;
    }
    if (form.referrer_credit_amount === '' || isNaN(Number(form.referrer_credit_amount))) {
      toast.error('Referrer Credit Amount is required'); return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        referee_credit_amount: parseFloat(form.referee_credit_amount) || 0,
        referrer_credit_amount: parseFloat(form.referrer_credit_amount) || 0,
        start_date: form.start_date || null,
        end_date:   form.end_date   || null,
      };
      if (initial?.id) {
        await axios.put(`${API}/admin/client-signup-offers/${initial.id}`, payload);
      } else {
        await axios.post(`${API}/admin/client-signup-offers`, payload);
      }
      toast.success(`Signup Offer ${initial?.id ? 'updated' : 'created'}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Edit Signup Offer' : 'Create Signup Offer'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-xs text-blue-700">
            <strong>How it works:</strong> When a Direct Client signs up using another client's referral link,
            the <strong>Referee</strong> gets instant wallet credit. When the Referee activates their first service,
            the <strong>Referrer</strong> gets a delayed reward.
          </div>

          <div className="space-y-1.5">
            <Label>Offer Name <span className="text-red-500">*</span></Label>
            <Input placeholder="e.g. Referral Bonus 2026" value={form.name}
              onChange={e => set('name', e.target.value)} data-testid="signup-offer-name" />
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={2} placeholder="Optional description…"
              value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
            <div className="space-y-1.5">
              <Label className="text-emerald-800 font-semibold text-sm">Referee Instant Credit <span className="text-red-500">*</span></Label>
              <p className="text-xs text-emerald-600">Credited instantly when new client signs up via referral</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium text-sm">₹</span>
                <Input type="number" min="0" placeholder="500" className="pl-7"
                  value={form.referee_credit_amount}
                  onChange={e => set('referee_credit_amount', e.target.value)}
                  data-testid="signup-offer-referee-credit" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-blue-800 font-semibold text-sm">Referrer Delayed Reward <span className="text-red-500">*</span></Label>
              <p className="text-xs text-blue-600">Credited to referrer when referee's first service activates</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium text-sm">₹</span>
                <Input type="number" min="0" placeholder="500" className="pl-7"
                  value={form.referrer_credit_amount}
                  onChange={e => set('referrer_credit_amount', e.target.value)}
                  data-testid="signup-offer-referrer-credit" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)}
                data-testid="signup-offer-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label>End Date</Label>
              <Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)}
                data-testid="signup-offer-end-date" />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
            <Switch checked={form.is_active} onCheckedChange={v => set('is_active', v)} id="so-active" />
            <Label htmlFor="so-active" className="cursor-pointer font-medium">
              {form.is_active ? 'Active' : 'Inactive'}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="signup-offer-save-btn">
            {saving ? 'Saving…' : (initial?.id ? 'Update Offer' : 'Create Offer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ════════════════════════════════════════════════════════════
   CLIENT SIGNUP OFFERS TAB
════════════════════════════════════════════════════════════ */
function ClientSignupOffersTab({ onCountChange }) {
  const [offers, setOffers]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dialog, setDialog]       = useState(null);
  const [deleteTarget, setDelete] = useState(null);
  const [deleting, setDeleting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await axios.get(`${API}/admin/client-signup-offers`)).data;
      setOffers(data);
      onCountChange(data.length);
    } catch { toast.error('Failed to load signup offers'); }
    finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (offer) => {
    try {
      const res = await axios.patch(`${API}/admin/client-signup-offers/${offer.id}/toggle`);
      setOffers(prev => prev.map(o => o.id === offer.id ? { ...o, is_active: res.data.is_active } : o));
      toast.success(res.data.is_active ? 'Offer activated' : 'Offer deactivated');
    } catch { toast.error('Toggle failed'); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/client-signup-offers/${deleteTarget.id}`);
      setOffers(prev => prev.filter(o => o.id !== deleteTarget.id));
      onCountChange(offers.length - 1);
      toast.success('Offer deleted');
      setDelete(null);
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{offers.length} offer{offers.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => setDialog('create')} data-testid="create-signup-offer-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Offer
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : offers.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No signup offers yet</p>
          <p className="text-sm mt-1">Create a referral bonus offer to configure signup rewards</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Offer', 'Referee Credit', 'Referrer Reward', 'Dates', 'Status', 'Active', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {offers.map(offer => (
                <tr key={offer.id} className="hover:bg-slate-50 transition-colors" data-testid={`signup-offer-row-${offer.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{offer.name}</div>
                    {offer.description && <div className="text-xs text-slate-400 truncate max-w-[140px]">{offer.description}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-semibold">
                      ₹{(offer.referee_credit_amount || 0).toLocaleString('en-IN')} instant
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-semibold">
                      ₹{(offer.referrer_credit_amount || 0).toLocaleString('en-IN')} on activation
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                    {offer.start_date ? offer.start_date.split('T')[0] : '—'}<br />
                    {offer.end_date   ? offer.end_date.split('T')[0]   : '—'}
                  </td>
                  <td className="px-4 py-3"><StatusBadge offer={offer} /></td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(offer)} className="focus:outline-none" data-testid={`signup-toggle-${offer.id}`}>
                      {offer.is_active
                        ? <ToggleRight className="h-6 w-6 text-emerald-500" />
                        : <ToggleLeft  className="h-6 w-6 text-slate-300" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialog(offer)} data-testid={`signup-edit-${offer.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDelete(offer)} data-testid={`signup-delete-${offer.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ClientSignupOfferDialog
        open={dialog !== null}
        initial={dialog === 'create' ? null : dialog}
        onClose={() => setDialog(null)}
        onSaved={load}
      />
      <DeleteConfirm open={deleteTarget !== null} name={deleteTarget?.name}
        onConfirm={remove} onClose={() => setDelete(null)} loading={deleting} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   DIRECT CLIENT SIGNUP OFFER DIALOG
════════════════════════════════════════════════════════════ */
const EMPTY_DIRECT_SIGNUP = {
  name: '', title: '', description: '',
  wallet_credit_amount: '500',
  is_active: true,
  start_date: '', end_date: '',
};

function DirectClientSignupOfferDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_DIRECT_SIGNUP);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      ...EMPTY_DIRECT_SIGNUP, ...initial,
      wallet_credit_amount: initial.wallet_credit_amount ?? '',
      start_date: initial.start_date ? initial.start_date.split('T')[0] : '',
      end_date:   initial.end_date   ? initial.end_date.split('T')[0]   : '',
    } : EMPTY_DIRECT_SIGNUP);
  }, [initial, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) { toast.error('Offer Name is required'); return false; }
    if (form.wallet_credit_amount === '' || isNaN(Number(form.wallet_credit_amount)) || Number(form.wallet_credit_amount) <= 0) {
      toast.error('Wallet Credit Amount is required and must be > 0'); return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        wallet_credit_amount: parseFloat(form.wallet_credit_amount) || 0,
        start_date: form.start_date || null,
        end_date:   form.end_date   || null,
      };
      if (initial?.id) {
        await axios.put(`${API}/admin/direct-client-signup-offers/${initial.id}`, payload);
      } else {
        await axios.post(`${API}/admin/direct-client-signup-offers`, payload);
      }
      toast.success(`Offer ${initial?.id ? 'updated' : 'created'}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Edit Signup Offer' : 'Create Signup Offer'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-xs text-emerald-700">
            <strong>How it works:</strong> When a new Direct Client signs up (no referral needed), 
            this amount is <strong>automatically credited</strong> to their wallet instantly on signup.
          </div>

          <div className="space-y-1.5">
            <Label>Offer Name <span className="text-red-500">*</span></Label>
            <Input placeholder="e.g. New Year Welcome Offer" value={form.name}
              onChange={e => set('name', e.target.value)} data-testid="ds-offer-name" />
          </div>

          <div className="space-y-1.5">
            <Label>Display Title</Label>
            <Input placeholder="e.g. Welcome Gift for New Clients" value={form.title}
              onChange={e => set('title', e.target.value)} data-testid="ds-offer-title" />
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={2} placeholder="Optional message shown to clients…"
              value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div className="space-y-1.5 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
            <Label className="text-emerald-800 font-semibold">Wallet Credit Amount (₹) <span className="text-red-500">*</span></Label>
            <p className="text-xs text-emerald-600">Credited to every new Direct Client wallet on signup</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium text-sm">₹</span>
              <Input type="number" min="1" placeholder="500" className="pl-7"
                value={form.wallet_credit_amount}
                onChange={e => set('wallet_credit_amount', e.target.value)}
                data-testid="ds-offer-credit-amount" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)}
                data-testid="ds-offer-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label>End Date</Label>
              <Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)}
                data-testid="ds-offer-end-date" />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
            <Switch checked={form.is_active} onCheckedChange={v => set('is_active', v)} id="ds-active" />
            <Label htmlFor="ds-active" className="cursor-pointer font-medium">
              {form.is_active ? 'Active' : 'Inactive'}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="ds-offer-save-btn">
            {saving ? 'Saving…' : (initial?.id ? 'Update Offer' : 'Create Offer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ════════════════════════════════════════════════════════════
   DIRECT CLIENT SIGNUP OFFERS TAB
════════════════════════════════════════════════════════════ */
function DirectClientSignupOffersTab({ onCountChange }) {
  const [offers, setOffers]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dialog, setDialog]       = useState(null);
  const [deleteTarget, setDelete] = useState(null);
  const [deleting, setDeleting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await axios.get(`${API}/admin/direct-client-signup-offers`)).data;
      setOffers(data);
      onCountChange(data.length);
    } catch { toast.error('Failed to load signup offers'); }
    finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (offer) => {
    try {
      const res = await axios.patch(`${API}/admin/direct-client-signup-offers/${offer.id}/toggle`);
      setOffers(prev => prev.map(o => o.id === offer.id ? { ...o, is_active: res.data.is_active } : o));
      toast.success(res.data.is_active ? 'Offer activated' : 'Offer deactivated');
    } catch { toast.error('Toggle failed'); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/direct-client-signup-offers/${deleteTarget.id}`);
      setOffers(prev => prev.filter(o => o.id !== deleteTarget.id));
      onCountChange(offers.length - 1);
      toast.success('Offer deleted');
      setDelete(null);
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{offers.length} offer{offers.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => setDialog('create')} data-testid="create-ds-offer-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Offer
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : offers.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Zap className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No client signup offers yet</p>
          <p className="text-sm mt-1">Create an offer to automatically credit new Direct Clients on signup</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Offer', 'Wallet Credit', 'Dates', 'Status', 'Active', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {offers.map(offer => (
                <tr key={offer.id} className="hover:bg-slate-50 transition-colors" data-testid={`ds-offer-row-${offer.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{offer.name}</div>
                    {offer.title && <div className="text-xs text-slate-400 truncate max-w-[150px]">{offer.title}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-bold">
                      ₹{(offer.wallet_credit_amount || 0).toLocaleString('en-IN')} instant
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                    {offer.start_date ? offer.start_date.split('T')[0] : '—'}<br />
                    {offer.end_date   ? offer.end_date.split('T')[0]   : '—'}
                  </td>
                  <td className="px-4 py-3"><StatusBadge offer={offer} /></td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(offer)} className="focus:outline-none" data-testid={`ds-toggle-${offer.id}`}>
                      {offer.is_active
                        ? <ToggleRight className="h-6 w-6 text-emerald-500" />
                        : <ToggleLeft  className="h-6 w-6 text-slate-300" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialog(offer)} data-testid={`ds-edit-${offer.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDelete(offer)} data-testid={`ds-delete-${offer.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DirectClientSignupOfferDialog
        open={dialog !== null}
        initial={dialog === 'create' ? null : dialog}
        onClose={() => setDialog(null)}
        onSaved={load}
      />
      <DeleteConfirm open={deleteTarget !== null} name={deleteTarget?.name}
        onConfirm={remove} onClose={() => setDelete(null)} loading={deleting} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════ */
export default function AdminOffers() {
  const [partnerCount, setPartnerCount]   = useState(null);
  const [serviceCount, setServiceCount]   = useState(null);
  const [signupCount,  setSignupCount]    = useState(null);
  const [dsCount,      setDsCount]        = useState(null);

  return (
    <DashboardLayout role="admin">
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Offer Management</h1>
          <p className="text-sm text-slate-500 mt-1">Create and manage promotional offers for partners and clients</p>
        </div>

        <Card>
          <CardContent className="p-0">
            <Tabs defaultValue="partner" className="w-full">
              <div className="border-b border-slate-200 px-6 pt-4">
                <TabsList className="bg-transparent gap-0 h-auto p-0">
                  <TabsTrigger
                    value="partner"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:bg-transparent px-4 pb-3 text-sm font-medium"
                    data-testid="tab-partner-offers"
                  >
                    Partner Offers
                    {partnerCount !== null && <span className="ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">{partnerCount}</span>}
                  </TabsTrigger>
                  <TabsTrigger
                    value="service"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:bg-transparent px-4 pb-3 text-sm font-medium"
                    data-testid="tab-service-offers"
                  >
                    Service Offers
                    {serviceCount !== null && <span className="ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">{serviceCount}</span>}
                  </TabsTrigger>
                  <TabsTrigger
                    value="signup"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:bg-transparent px-4 pb-3 text-sm font-medium"
                    data-testid="tab-signup-offers"
                  >
                    Referral Bonuses
                    {signupCount !== null && <span className="ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">{signupCount}</span>}
                  </TabsTrigger>
                  <TabsTrigger
                    value="client-signup"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:bg-transparent px-4 pb-3 text-sm font-medium"
                    data-testid="tab-client-signup-offers"
                  >
                    Client Signup
                    {dsCount !== null && <span className="ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">{dsCount}</span>}
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="partner" className="p-6 mt-0">
                <PartnerOffersTab onCountChange={setPartnerCount} />
              </TabsContent>
              <TabsContent value="service" className="p-6 mt-0">
                <ClientOffersTab onCountChange={setServiceCount} />
              </TabsContent>
              <TabsContent value="signup" className="p-6 mt-0">
                <ClientSignupOffersTab onCountChange={setSignupCount} />
              </TabsContent>
              <TabsContent value="client-signup" className="p-6 mt-0">
                <DirectClientSignupOffersTab onCountChange={setDsCount} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
